import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import { canManageStaff, getPermissionErrorMessage, getAvailableRoles, canAssignRole, getRoleAssignmentErrorMessage } from '../services/staffPermissionService';
import { uploadUserPhoto } from '../services/storageService';
import TopBar from '../components/TopBar';
import Input from '../components/Input';
import PhoneInput from '../components/PhoneInput';
import Button from '../components/Button';
import defaultProfilePic from '../assets/default-profile.jpg';

interface CreateStaffData {
  email: string;
  password?: string; 
  firstName: string;
  lastName: string;
  phone: string;
  businessId: string;
  role: string;
  photoUrl?: string; 
}

interface CreateStaffResult {
  success: boolean;
  userId: string;
}

interface Business {
  id: string;
  name: string;
}

interface LocationState {
  email: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
  
  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #333;
`;

const NamePhotoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const PhotoContainer = styled.div`
  flex-shrink: 0;
  order: 1;
  
  @media (min-width: 768px) {
    order: 1;
  }
`;

const ProfilePhoto = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  
  &::after {
    content: 'Change Photo';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 5px;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NameContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  order: 2;
`;

const NameRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FormColumn = styled.div`
  flex: 1;
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const PhoneSection = styled.div`
  margin-top: 4px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ButtonContainer = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
`;

const RoleContainer = styled.div`
  margin-bottom: 20px;
`;

const RoleLabel = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
`;

const RoleSelect = styled.select`
  width: 100%;
  padding: 14px;
  font-size: 16px;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  font-family: 'Montserrat', sans-serif;
  outline: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  color: #333;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 16px;
`;

const ErrorMessage = styled.div`
  color: #e53935;
  padding: 16px;
  background-color: #ffebee;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  color: #43a047;
  padding: 16px;
  background-color: #e8f5e9;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const PermissionErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 20px;
  background-color: #f9f9f9;
  border-radius: 12px;
  margin-top: 40px;
`;

const PermissionErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const PermissionErrorMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  max-width: 400px;
  line-height: 1.5;
`;

const AddNewStaffPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessId } = useParams<{ businessId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('staff');
  const [photoUrl, setPhotoUrl] = useState(''); 
  const [photoFile, setPhotoFile] = useState<File | null>(null);  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [permissionError, setPermissionError] = useState<string>('');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const locationState = location.state as LocationState | undefined;
  const prefilledEmail = locationState?.email || '';
  
  // Set the prefilled email when component mounts
  useEffect(() => {
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [prefilledEmail]);
  
  const functions = getFunctions();
  const createStaffAccountCallable = httpsCallable<CreateStaffData, CreateStaffResult>(functions, 'createStaffAccount');
  
  useEffect(() => {
    const fetchData = async () => {
      if (!businessId || !locationState) {
        navigate(`/business/${businessId}/staff/lookup`);
        return;
      }      try {
        setLoading(true);
        const currentUser = await getCurrentUser();
        setUser(currentUser);

        if (!currentUser?.id) {
          navigate('/login');
          return;
        }        // Check if user has permission to manage staff
        const hasPermission = await canManageStaff(currentUser.id, businessId);
        if (!hasPermission) {
          setPermissionError(getPermissionErrorMessage('add-staff'));
          setLoading(false);
          return;
        }

        // Get available roles for this user
        const roles = await getAvailableRoles(currentUser.id, businessId);
        setAvailableRoles(roles);
        
        // Set default role to the lowest available role
        if (roles.length > 0) {
          setRole(roles[roles.length - 1]); // Last item is the lowest role
        }

        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        
        if (businessDoc.exists()) {
          setBusiness({
            id: businessDoc.id,
            name: businessDoc.data().name
          });
        } else {
          console.error("Business not found");
          navigate('/businesses');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId, navigate, locationState]);
  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleNavigateToMyBusinesses = () => {
    navigate('/businesses');
  };
  
  const handleNavigateToSettings = () => {
    navigate(`/business/${businessId}/settings`);
  };
  
  const handleNavigateToStaff = () => {
    navigate(`/business/${businessId}/staff`);
  };
  
  const handlePhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      setPhotoFile(files[0]);
      setPhotoUrl(URL.createObjectURL(files[0]));
    }
  };

  const validateForm = (): boolean => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password) {
      setError('Please fill in all required fields.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    return true;
  };
    const handleAddStaff = async () => {
    if (!businessId) return;
    
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      return;
    }

    // Check if user can assign the selected role
    const currentUser = await getCurrentUser();
    if (currentUser?.id) {
      const canAssign = await canAssignRole(currentUser.id, businessId, role as any);
      if (!canAssign) {
        const userRole = await import('../services/staffPermissionService').then(service => 
          service.getUserRole(currentUser.id, businessId)
        );
        setError(getRoleAssignmentErrorMessage(userRole, role as any));
        return;
      }
    }
    
    setSaving(true);
    
    try {
      let finalPhotoUrl = '';
      if (photoFile) {
        const tempUserIdForPhoto = `pending-${Date.now()}`;
        console.log(`Uploading photo for pending user...`);
        finalPhotoUrl = await uploadUserPhoto(tempUserIdForPhoto, photoFile);
        console.log(`Photo uploaded, URL: ${finalPhotoUrl}`);
      }

      const staffData: CreateStaffData = {
        email,
        password,
        firstName,
        lastName,
        phone,
        businessId,
        role,
        photoUrl: finalPhotoUrl || undefined
      };

      console.log('Calling createStaffAccount with data:', staffData);

      const result: HttpsCallableResult<CreateStaffResult> = await createStaffAccountCallable(staffData);

      console.log('Cloud Function result:', result.data);

      if (result.data.success) {
        setSuccess(`Staff member ${firstName} ${lastName} added successfully (ID: ${result.data.userId})`);
        setTimeout(() => {
          navigate(`/business/${businessId}/staff`);
        }, 2500);
      } else {
        setError('Failed to add staff member. The function reported failure.');
      }

    } catch (error: any) {
      console.error('Error calling createStaffAccount function:', error);
      
      if (error.code === 'functions/already-exists') {
        setError('Email address is already in use by another account.');
      } else if (error.code === 'functions/permission-denied') {
        setError('Permission denied. You might not be the manager of this business.');
      } else if (error.code === 'functions/invalid-argument') {
        setError(`Invalid data provided: ${error.message || 'Please check the fields.'}`);
      } else if (error.code === 'functions/unauthenticated') {
         setError('Authentication error. Please log in again.');
      } else {
        setError('An unexpected error occurred while adding the staff member. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };
    if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          <LoadingContainer>Loading...</LoadingContainer>
        </ContentWrapper>
      </Container>
    );
  }

  // Handle permission error
  if (permissionError) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Access Denied</title>
        </Helmet>
        <Container>
          <ContentWrapper>
            <TopBar user={user} />
            <PermissionErrorContainer>
              <PermissionErrorTitle>Access Denied</PermissionErrorTitle>
              <PermissionErrorMessage>{permissionError}</PermissionErrorMessage>
              <Button 
                text="Back to Team" 
                onClick={() => navigate(`/business/${businessId}/staff`)}
                bgColor="#000"
                textColor="#fff"
                width="auto"
                minWidth="180px"
              />
            </PermissionErrorContainer>
          </ContentWrapper>
        </Container>
      </>
    );
  }
  
  if (!locationState) {
    return null; 
  }
  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Add Staff</title>
        <meta name="description" content={`${APP_NAME} - Add Staff`} />
      </Helmet>
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          
          <PageTitle>Add Team Member</PageTitle>
        
        <Breadcrumbs>
          <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>Businesses</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToSettings}>
            <BreadcrumbText>{business?.name || 'Business'}</BreadcrumbText>
          </BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToStaff}>Team</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <span>Add New</span>
        </Breadcrumbs>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        {permissionError && (
          <PermissionErrorContainer>
            <PermissionErrorTitle>Permission Denied</PermissionErrorTitle>
            <PermissionErrorMessage>{permissionError}</PermissionErrorMessage>
          </PermissionErrorContainer>
        )}
        
        <FormContainer>
          <FormTitle>Create new user account</FormTitle>
          
          <NamePhotoSection>
            <PhotoContainer>
              <ProfilePhoto onClick={handlePhotoClick}>
                <img 
                  src={photoUrl || defaultProfilePic} 
                  alt="Profile" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultProfilePic;
                  }}
                />
              </ProfilePhoto>
              <HiddenFileInput 
                type="file" 
                accept="image/*" 
                ref={fileInputRef} 
                onChange={handlePhotoChange} 
              />
            </PhotoContainer>
            
            <NameContainer>
              <NameRow>
                <FormColumn>
                  <Input 
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    name="firstName"
                    label="First Name"
                    required
                    secondary
                  />
                </FormColumn>
                <FormColumn>
                  <Input 
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    name="lastName"
                    label="Last Name"
                    required
                    secondary
                  />
                </FormColumn>
              </NameRow>
              
                <PhoneSection>
                <PhoneInput
                  value={phone}
                  onChange={(value) => setPhone(value)}
                  name="phone"
                  label="Phone Number (Optional)"
                  required={false}
                  secondary
                />
                </PhoneSection>
            </NameContainer>
          </NamePhotoSection>
          
          <FormSection>
            <Input 
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email Address"
              name="email"
              label="Email"
              required
              readOnly
              secondary
            />
          </FormSection>
          
          <FormSection>
            <Input 
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (min 6 characters)"
              name="password"
              label="Password"
              required
              secondary
            />
          </FormSection>            <RoleContainer>
            <RoleLabel>Role</RoleLabel>
            <RoleSelect
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {availableRoles.map((roleOption) => (
                <option key={roleOption} value={roleOption}>
                  {roleOption.charAt(0).toUpperCase() + roleOption.slice(1)}
                </option>
              ))}
            </RoleSelect>
          </RoleContainer>
          
        </FormContainer>
          <ButtonContainer>
            <Button
              text="Add"
              onClick={handleAddStaff}
              loading={saving}
              bgColor="#000"
              textColor="#fff"
              width="auto"
              minWidth="150px"
            />          </ButtonContainer>
      </ContentWrapper>
    </Container>
    </>
  );
};

export default AddNewStaffPage;